import { useState, useEffect, useRef } from "preact/compat";
import { useQuery } from "@apollo/client";
import useLocalStorageState from "use-local-storage-state";
import MENU_QUERY from "@/queries/menuQuery";

/**
 * Custom React hook for managing product slider.
 *
 * @param {Object} settings - The settings for the product slider.
 * @param {number} settings.limit - The maximum number of products to display.
 * @param {string} settings.sort - The sorting method for the products.
 * @param {number} settings.index - The index of the current slide.
 * @param {string} settings.menuSection - The section of the menu to display products from.
 * @param {string} settings.customSectionName - The name of the custom section to display products from.
 * @param {string} settings.category - The category of products to display.
 * @param {string} settings.brand - The brand of products to display.
 * @param {string} settings.strainType - The strain type of products to display.
 * @param {boolean} settings.portal - Whether to display the product slider in a portal.
 * @param {string|boolean} settings.retailerIDProp - The ID of the retailer to display products from. If false, the retailer ID is retrieved from local storage.
 * @param {string} settings.specialID - The ID of the special to display products from.
 *
 * @returns {Object} The state and functions for managing the product slider.
 */
export function useProductSlider( settings ) {
	const {
		limit,
		sort,
		index,
		menuSection = 'none',
		customSectionName,
		category,
		brand,
		strainType,
		portal = true,
		retailerID: retailerIDProp = false,
		specialID = false
	} = settings;

	const [retailerObject, setRetailerObject] = useLocalStorageState("dutchie_retailer");
	// If retailerIDProp is not set, get retailerID from localStorage
	let retailerID = retailerIDProp ? retailerIDProp : retailerObject?.id;

	const retailer = dutchie.retailers.find(
		(location) => location.id === retailerObject?.id
	);

	let brandId = null;
	// If brand is an object (User selected a brand in ProductSlider Module), get the brand ID for the current retailer
	brandId = typeof brand === 'object' ? (brand[retailerID] || brand[Object.keys(brand)[0]] ) : brand;

	let filter = {
		... ( category ? { category: category } : {} ),
		... ( brandId ? { brandId: brandId } : {} ),
		... ( strainType ? { strainType: strainType } : {} ),
		... ( menuSection !== 'none' || customSectionName ? {
			menuSection: {
				type: menuSection,
				... ( menuSection === 'CUSTOM_SECTION' ? { name: customSectionName } : {}),
				... ( specialID ? { specialId: specialID } : {} ),
			}
		} : {} ),
	};
	
	const [products, setProducts] = useState([]);
	const prevBtnRef = useRef(null);
	const nextBtnRef = useRef(null);
	let loopEnabled = false;
	const [status, setStatus] = useState('loading');
	const [inViewport, setInViewport] = useState(false);
	const sliderRef = useRef(null);

	const handleIntersection = (entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				setInViewport(true);
			}
		});
	};

	useEffect(() => {
		const options = {
			root: null,
			rootMargin: "0px",
			threshold: 0.1, // Adjust this threshold as needed
		};

		const observer = new IntersectionObserver(handleIntersection, options);
		if (sliderRef.current) {
			observer.observe(sliderRef.current);
		}

		// Cleanup the observer when the component unmounts
		return () => {
			if (sliderRef.current) {
				observer.unobserve(sliderRef.current);
			}
		};
	}, []);

	const { loading, error, data } = useQuery(MENU_QUERY, {
		onCompleted: function (data) {
			setProducts(data.menu.products);
			setStatus('complete');
		},
		variables: {
			retailerId: retailerID,
			limit: limit,
			offset: 0,
			sort: sort,
			filter: filter
		},
		skip: !retailerID || !inViewport,
	});

	if (error) {
		console.log(`Error! ${error.message}`);
		return null;
	}

	return { products, loading, status, retailer, sliderRef, inViewport, prevBtnRef, nextBtnRef, loopEnabled }
}